import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface EnvironmentalData {
  responses: AssessmentResponse[];
  scores: {
    overall: number;
    rawScore: number;
    transformedScore: number;
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const EnvironmentalAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // WHOQOL-BREF Environmental Domain (8 items)
  const questions = [
    { id: '1', question: 'How safe do you feel in your daily life?', type: 'satisfaction' },
    { id: '2', question: 'How healthy is your physical environment (pollution, noise, climate)?', type: 'satisfaction' },
    { id: '3', question: 'Do you have enough money to meet your needs?', type: 'satisfaction' },
    { id: '4', question: 'How available is the information you need in your daily life?', type: 'satisfaction' },
    { id: '5', question: 'To what extent do you have opportunities for leisure activities?', type: 'satisfaction' },
    { id: '6', question: 'How satisfied are you with the conditions of your living place?', type: 'satisfaction' },
    { id: '7', question: 'How satisfied are you with your access to health services?', type: 'satisfaction' },
    { id: '8', question: 'How satisfied are you with your transport?', type: 'satisfaction' }
  ];

  const totalQuestions = questions.length;

  // Answer options (1-5 satisfaction scale)
  const answerOptions = [
    { value: 1, label: 'Very dissatisfied' },
    { value: 2, label: 'Dissatisfied' },
    { value: 3, label: 'Neither satisfied nor dissatisfied' },
    { value: 4, label: 'Satisfied' },
    { value: 5, label: 'Very satisfied' }
  ];

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;
    
    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      return [...filtered, newResponse];
    });
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScores = (responses: AssessmentResponse[]) => {
    // Sum all 8 items (range: 8-40)
    const rawScore = responses.reduce((sum, r) => sum + r.answer, 0);
    
    // Transform to 0-100: ((Raw - 8) / 32) × 100
    const transformedScore = Math.round(((rawScore - 8) / 32) * 100);
    
    return {
      overall: transformedScore,
      rawScore: rawScore,
      transformedScore: transformedScore
    };
  };

  const interpretResults = (scores: any) => {
    const { transformedScore, rawScore } = scores;
    
    let level: 'Low' | 'Moderate' | 'High';
    let interpretation: string;
    let recommendations: string[] = [];
    
    // Interpretation based on transformed score (0-100)
    // 0-33 = Poor
    // 34-66 = Moderate
    // 67-100 = Good environmental QoL
    
    if (transformedScore >= 67) {
      level = 'High';
      interpretation = `Good Environmental Quality of Life (Score: ${transformedScore}/100, Raw: ${rawScore}/40). You experience high satisfaction with your environmental conditions. You feel safe in your daily life, have a healthy physical environment, sufficient financial resources to meet your needs, access to necessary information, opportunities for leisure, satisfactory living conditions, good access to health services, and adequate transport. This indicates strong environmental support for your overall well-being and quality of life.`;
      recommendations = [
        'Continue maintaining your current living environment and lifestyle',
        'Share knowledge about resources and opportunities with others in your community',
        'Consider how you can contribute to improving environmental conditions for others',
        'Stay engaged with community services and health resources',
        'Maintain your work-life balance and leisure activities',
        'Keep informed about changes or improvements in your community services'
      ];
    } else if (transformedScore >= 34) {
      level = 'Moderate';
      interpretation = `Moderate Environmental Quality of Life (Score: ${transformedScore}/100, Raw: ${rawScore}/40). You experience moderate satisfaction with your environmental conditions. While some aspects of your environment support your well-being (safety, physical environment, resources, services), there are areas where you may feel less satisfied or face challenges. You may have some concerns about financial resources, access to information, leisure opportunities, living conditions, health services, or transport. Improving these environmental factors can enhance your overall quality of life.`;
      recommendations = [
        'Identify which environmental factors are most challenging for you (safety, finances, health access, etc.)',
        'Explore community resources and services that might address your environmental needs',
        'Research government or community programs that provide support (housing, transport, health services)',
        'Consider ways to improve your living environment within your current resources',
        'Connect with local community centers or organizations for information and support',
        'Develop a plan to address your most pressing environmental concerns',
        'Seek advice from social workers or community advocates if needed',
        'Look for low-cost or free leisure activities in your community'
      ];
    } else {
      level = 'Low';
      interpretation = `Poor Environmental Quality of Life (Score: ${transformedScore}/100, Raw: ${rawScore}/40). ⚠️ You experience low satisfaction with your environmental conditions, which significantly impacts your overall well-being. You may feel unsafe in daily life, have an unhealthy physical environment, lack sufficient financial resources, have limited access to information or leisure activities, unsatisfactory living conditions, poor access to health services, or inadequate transport. These environmental stressors can negatively affect your physical health, mental health, and overall quality of life. It is important to seek support and explore resources to improve your environmental conditions.`;
      recommendations = [
        '⚠️ PRIORITY: Seek assistance from social services or community organizations',
        'Contact local government agencies about housing, safety, or transport assistance programs',
        'Explore emergency financial assistance if you lack resources to meet basic needs',
        'Connect with community health centers for affordable or free health services',
        'Research public transport options or community transport assistance programs',
        'Look into housing assistance, subsidies, or relocation support if living conditions are poor',
        'Consult with social workers about comprehensive support for multiple environmental challenges',
        'Prioritize addressing safety concerns first (shelter, personal safety, food security)',
        'Document environmental concerns (pollution, noise) and report to appropriate authorities',
        'Build connections with neighbors or community groups for mutual support',
        'Remember that improving environmental conditions is often a gradual process requiring external support'
      ];
    }
    
    return { level, interpretation, recommendations };
  };

  const handleSubmit = async () => {
    if (responses.length < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const scores = calculateScores(responses);
      const results = interpretResults(scores);

      const assessmentData: EnvironmentalData = {
        responses,
        scores,
        results
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.ENVIRONMENTAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        throw new Error('Failed to submit assessment');
      }

      const result = await response.json();
      
      // Clear Insights page cache to force refresh
      const userId = JSON.parse(localStorage.getItem('user') || '{}').id;
      const insightsCacheKey = userId ? `lifepsensor_insights_cache_${userId}` : 'lifepsensor_insights_cache';
      localStorage.removeItem(insightsCacheKey);
      
      // Notify InsightsPage about the update
      assessmentUpdateNotifier.notifyUpdate('environmental');
      
      // Navigate to completion page
      navigate('/panel/learn/environmental-assessment/completion', {
        state: { assessmentResult: result.data }
      });

    } catch (error) {

      let errorMessage = 'Error submitting assessment. Please try again.';
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Environmental Assessment</h1>
              <p>Processing your responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Environmental Assessment</h1>
              <p>Loading assessment questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel environmental-panel"
        >
          <div className="assessment-header">
            <h1>Environmental Assessment</h1>
            <p className="assessment-subtitle">
              WHOQOL-BREF Environmental Domain
            </p>
            <p className="assessment-tool-info">
              Evaluating your environmental quality of life - safety, resources, services, and opportunities
            </p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            <h2 className="question-title">
              {currentQuestionData.question}
            </h2>
            <p className="question-instruction">
              Please rate your satisfaction considering your current life situation in the last 2 weeks.
            </p>

            <div className="answer-options">
              {answerOptions.map((option) => (
                <label key={option.value} className="answer-option">
                  <input
                    type="radio"
                    name={`question-${currentQuestion}`}
                    value={option.value}
                    checked={currentResponse?.answer === option.value}
                    onChange={(e) => saveCurrentResponse(parseInt(e.target.value))}
                  />
                  <span className="option-text">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={() => {
                if (currentQuestion === 1) {
                  navigate('/panel/learn/');
                  return;
                }
                handleBack();
              }}
              disabled={false}
            >
              Back
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={!currentResponse}
            >
              {currentQuestion === totalQuestions ? 'Complete Assessment' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalAssessmentFormPage;



